import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail } from "lucide-react";
import { COMMITMENT_ICON_SRCS } from "../components/icons/CommitmentIcons";
import { IMAGES, COMMITMENTS } from "../lib/content";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { Marquee } from "../components/Marquee";
import { WelcomeVideo } from "../components/WelcomeVideo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const MAILTO = "mailto:pete@peteashbycoaching.com";

const COMMITMENT_ICONS = COMMITMENT_ICON_SRCS;

const WAYS = [
  {
    title: "The Method",
    body: "The ten Board-level commitments that stop fraud and dishonesty gaining a foothold.",
    image: IMAGES.glass,
    to: "/method",
    cta: "Explore the Method",
    testid: "way-ttm",
  },
  {
    title: "On Tough Trust",
    body: "Pete's films and thinking on trust, leadership and the culture that prevents fraud.",
    image: IMAGES.boardroomModern,
    to: "/on-leadership",
    cta: "Watch & read",
    testid: "way-coaching",
  },
  {
    title: "Coaching Pairs",
    body: "Leaders coaching each other in pairs, with a new partner every six months.",
    image: IMAGES.coachingPairs,
    to: "/coaching-pairs",
    cta: "Discover pairs",
    testid: "way-pairs",
  },
];

export const CtaBand = () => (
  <section className="py-24 bg-ivory">
    <div className="container-px">
      <Reveal>
        <div className="relative overflow-hidden bg-windsor text-ivory px-8 md:px-16 py-16 text-center">
          <img
            src="/tt-mark.png"
            alt=""
            aria-hidden
            data-testid="cta-band-watermark"
            className="pointer-events-none select-none absolute -right-10 -bottom-10 w-64 md:w-80 max-w-none opacity-10 mix-blend-luminosity"
          />
          <div className="relative z-10">
            <p className="overline !text-ivory">Start the conversation</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl mx-auto leading-tight">
              Bring Tough Trust to your Board.
            </h2>
            <a
              href={MAILTO}
              data-testid="cta-band-button"
              className="group relative inline-flex items-center gap-3 mt-9 bg-ivory pl-7 pr-2 py-2 rounded-full font-semibold text-base md:text-lg shadow-xl shadow-black/20 overflow-hidden transition-all duration-300 hover:scale-[1.04] hover:shadow-2xl hover:shadow-black/40"
            >
              <span
                aria-hidden
                className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"
              />
              <Mail size={18} className="relative z-10 text-windsor transition-colors duration-300 group-hover:text-ivory" />
              <span className="relative z-10 text-windsor transition-colors duration-300 group-hover:text-ivory">
                pete@peteashbycoaching.com
              </span>
              <span className="relative z-10 grid place-items-center w-11 h-11 rounded-full bg-windsor text-ivory transition-transform duration-300 group-hover:translate-x-0.5 group-hover:rotate-6">
                <ArrowRight size={20} />
              </span>
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={IMAGES.heroBg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/85 to-ink/55" />
        </div>
        <div className="container-px relative z-10 pt-28 pb-20">
          <div className="max-w-3xl animate-fade-up">
            <p className="overline !text-gold">The Tough Trust Method</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.05] tracking-tight mt-6">
              Fraud starts in dark corners.
              <br className="hidden sm:block" />{" "}
              <span className="text-windsor">Tough Trust shines the light.</span>
            </h1>
            <p className="text-ivory/75 text-lg sm:text-xl font-light mt-7 max-w-2xl leading-relaxed">
              A Board-level framework for the Economic Crime and Corporate Transparency Act 2023 –
              treating the prevention of fraud not as a tick-box exercise, but as a leadership
              challenge.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link
                to="/method"
                data-testid="hero-cta-commitments"
                className="bg-[#C2410C] text-ivory px-8 py-4 font-medium tracking-wide hover:bg-windsor-dark transition-colors inline-flex items-center justify-center gap-2"
              >
                Explore the Method <ArrowRight size={18} />
              </Link>
              <a
                href="#work"
                data-testid="hero-cta-work"
                className="border border-ivory/40 text-ivory px-8 py-4 font-medium tracking-wide hover:bg-ivory hover:text-ink transition-colors inline-flex items-center justify-center"
              >
                Work with Pete
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WELCOME VIDEO */}
      <WelcomeVideo src="/video-intro.mp4" poster="/video-intro.jpg" />

      {/* UNIFYING WORD */}
      <section className="py-24 md:py-32 bg-ink text-ivory" data-testid="unifying-word">
        <div className="container-px max-w-4xl">
          <Reveal>
            <p className="overline !text-gold">The unifying word</p>
            <p className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6">
              There's one word behind everything Pete does – and everything the Method
              stands for: <span className="text-windsor">trust</span>.
            </p>
            <p className="text-ivory/70 font-light text-lg mt-8 leading-relaxed max-w-2xl">
              Not the soft, assumed kind that quietly looks the other way – but trust that is tough
              enough to ask the hard questions, and honest enough to hear the answers. Trust that
              holds under pressure, because it is built in the small decisions, every single day.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ECCTA MANDATE */}
      <section className="bg-ivory text-ink py-24 md:py-32 border-b border-ink/10">
        <div className="container-px">
          <Reveal className="mb-12 lg:mb-16 max-w-4xl">
            <p className="overline">The new legal reality</p>
            <h2
              data-testid="legal-reality-heading"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02] tracking-tight mt-4"
            >
              Failure to prevent fraud: what it means for your Board
            </h2>
          </Reveal>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="space-y-6">
              <div>
                <p className="font-serif text-6xl md:text-7xl lg:text-8xl leading-none tracking-tight text-windsor">
                  £36m+
                </p>
                <p className="text-xl font-light mt-1 text-ink/80">annual turnover</p>
              </div>
              <div className="w-16 h-px bg-ink/15" />
              <div>
                <p className="font-serif text-6xl md:text-7xl lg:text-8xl leading-none tracking-tight text-windsor">
                  250+
                </p>
                <p className="text-xl font-light mt-1 text-ink/80">employees</p>
              </div>
              <div className="w-16 h-px bg-ink/15" />
              <div>
                <p className="font-serif text-6xl md:text-7xl lg:text-8xl leading-none tracking-tight text-windsor">
                  £18m+
                </p>
                <p className="text-xl font-light mt-1 text-ink/80">total assets</p>
              </div>
              <p data-testid="legal-reality-two-of-three" className="text-sm uppercase tracking-[0.2em] text-ink/60 pt-2">
                Any two of three
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 space-y-6 text-ink/75 text-lg md:text-xl font-light leading-relaxed">
            <p>
              Since 1 September 2025, large organisations can be held criminally liable under the
              Economic Crime and Corporate Transparency Act 2023 if someone working for them commits
              fraud intended to benefit the organisation. An organisation is in scope if it meets at
              least two of these three: more than 250 employees, more than £36 million turnover, more
              than £18 million in total assets.
            </p>
            <p>
              This means that Boards have to demonstrate a{" "}
              <Link
                to="/method"
                data-testid="legal-reality-method-link"
                className="font-medium text-ink underline decoration-[#C2410C] underline-offset-4 hover:text-[#C2410C] transition-colors"
              >
                "top level commitment"
              </Link>{" "}
              to put in place{" "}
              <span className="font-medium text-ink">"reasonable procedures"</span> to prevent fraud.
            </p>
            <p>
              If you don't, and fraud takes place that was justified as benefitting the organisation,
              the organisation could be held{" "}
              <span className="font-semibold text-windsor">criminally liable</span>.
            </p>
          </Reveal>
        </div>
          <Reveal className="mt-12">
            <a
              href="https://www.gov.uk/government/publications/offence-of-failure-to-prevent-fraud-introduced-by-eccta"
              target="_blank"
              rel="noopener"
              data-testid="legal-reality-guidance-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#C2410C] hover:text-windsor-dark transition-colors"
            >
              Read the government guidance <ArrowUpRight size={15} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* LEADERSHIP CHALLENGE + ST GEORGE'S */}
      <section className="bg-ivory text-ink pt-24 md:pt-32 pb-16 md:pb-20">
        <div className="container-px grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <img
                src={IMAGES.stGeorges}
                alt="Leaders in session at St George's House, Windsor Castle"
                loading="lazy"
                className="w-full h-[460px] object-cover border border-ink/10"
              />
              <p className="text-ink/45 text-sm mt-3 italic">
                Pete with one of the 200+ groups he has worked with in the Vicars' Hall, Windsor
                Castle.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <p className="overline">A leadership challenge</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02] tracking-tight mt-4">
              Not a job for the auditors.
            </h2>
            <div className="space-y-5 text-ink/80 text-lg font-light leading-relaxed mt-7">
              <p>
                Instead of just asking your auditors to review internal financial procedures, this is
                a real leadership challenge for the whole Board and the Executive team – to ensure
                that your internal culture is founded on the two key principles that sit at the heart
                of any effective anti-fraud strategy:{" "}
                <span className="text-windsor font-medium">truthfulness and transparency</span>.
              </p>
              <p>
                They are central to the Tough Trust Method, as this website shows – with its draft
                commitments to stopping fraud and dishonesty from ever gaining a foothold in your
                organisation.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* THE METHOD OVERVIEW */}
      <section className="py-24 md:py-28 bg-cloud" data-testid="method-overview">
        <div className="container-px">
          <Reveal className="max-w-2xl">
            <p className="overline">The Tough Trust Method</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink mt-4 leading-tight">
              Ten Board-level commitments.
            </h2>
            <p className="text-ink/80 font-light text-lg mt-5 leading-relaxed">
              A practical framework any Board can adopt – each point earned the hard way, and rewritten
              in daylight as the organisation learns.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {COMMITMENTS.map((c, i) => {
              const Icon = COMMITMENT_ICONS[i % COMMITMENT_ICONS.length];
              const isLast = i === COMMITMENTS.length - 1;
              return (
                <motion.div
                  key={c.n}
                  data-testid={`method-overview-item-${c.n}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className={`group flex items-start gap-4 bg-white border border-ink/10 p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-windsor/30 ${
                    isLast ? "lg:col-start-2" : ""
                  }`}
                >
                  <span className="shrink-0 w-14 h-14 grid place-items-center rounded-full bg-windsor/10 transition-colors duration-300 group-hover:bg-windsor/20">
                    <img
                      src={Icon}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className="w-7 h-7 object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                  </span>
                  <div className="min-w-0">
                    <span className="font-serif text-lg text-windsor leading-none">{c.n}</span>
                    <p className="font-serif text-lg text-ink leading-snug mt-1 transition-colors duration-300 group-hover:text-windsor">
                      {c.title}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <Reveal className="mt-10">
            <Link
              to="/method"
              data-testid="method-overview-link"
              className="inline-flex items-center gap-2 bg-[#C2410C] text-ivory px-8 py-4 font-medium hover:bg-windsor-dark transition-colors"
            >
              Explore the full Method <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WAYS TO ENGAGE */}
      <section id="work" className="py-24 md:py-32 bg-ivory scroll-mt-20">
        <div className="container-px">
          <Reveal>
            <SectionHeading
              overline="Work with Pete"
              title="Three ways to engage"
              intro="Initial conversations are always off the record."
            />
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {WAYS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <Link
                  to={w.to}
                  data-testid={w.testid}
                  className="group bg-white border border-ink/10 overflow-hidden hover:-translate-y-1 hover:border-windsor/40 transition-all duration-500 h-full flex flex-col"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={w.image}
                      alt={w.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="font-serif text-2xl text-ink leading-tight">{w.title}</h3>
                    <p className="text-ink/80 font-light leading-relaxed mt-3 flex-1">{w.body}</p>
                    <span className="inline-flex items-center gap-2 mt-6 text-windsor font-medium text-sm group-hover:gap-3 transition-all">
                      {w.cta} <ArrowUpRight size={16} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CtaBand />
    </div>
  );
}
